import React, { ChangeEvent, useEffect, useState } from 'react';
import { Switch, Tooltip, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import AccessGuard from '../../components/AccessGuard';
import TabPanel from '../../components/TabPanel';
import { USER_GROUP_ADMIN } from '../../types/User';
import ApiService from '../../ApiService';
import { DspInventorySupply } from '../../types/DspEntity';
import theme from '../../theme';
import Column from '../../components/Column';
import Row from '../../components/Row';
import { ITab } from './DspAdGroupDictsTab';
import CDGrid, { CustomPagination } from '../../components/CDGrid';
import { GridColDef, GridFooterContainer, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { AxiosPageableResponse } from '../../types/AxiosPageableResponse';

function DspInventorySupplyGridFooter(props: { totalElements: number; numberOfElements: number }) {
    return (
        <GridFooterContainer>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginRight: '10px' }}>
                <CustomPagination />
                <span>
                    Showing {props.numberOfElements} of {props.totalElements} records
                </span>
            </div>
        </GridFooterContainer>
    );
}

const DspInventorySupplyTab = ({ tab, activeTab }: { tab: ITab; activeTab: number }) => {
    const defaultPageSize: number = 50;
    const pageValueArray = [10, 20, 50, 75, 100];
    const [page, setPage] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [numberOfElements, setNumberOfElements] = useState(0);
    const [list, setList] = useState<DspInventorySupply[] | null>(null);
    const [pageSize, setPageSize] = React.useState(defaultPageSize);
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'id', sort: 'desc' }]);
    const handleChange = (event: SelectChangeEvent<string>) => {
        const value = parseInt(event?.target?.value);
        if (pageValueArray.includes(value)) {
            setPageSize(value);
        } else {
            // Handle invalid value or reset to default
            setPageSize(defaultPageSize);
        }
    };

    // Handle switch toggle
    const handleSwitchChange = (id: number) => (event: ChangeEvent<HTMLInputElement>) => {
        if (!list) return;
        const checked = event.target.checked;
        debugger;

        setList(
            list.map((row) => {
                if (row.id === id) {
                    const newRow = { ...row, exclude: checked };
                    ApiService.updateDspInventorySupply(newRow);
                    return newRow;
                }
                return row;
            })
        );
    };

    function onPageChange(model: GridPaginationModel) {
        setPage(model.page);
    }

    const fetchData = async () => {
        const response: AxiosPageableResponse<DspInventorySupply> = await ApiService.getDspInventorySupplies(
            page,
            pageSize,
            sortModel
        );
        if (response.status === 200) {
            setList(response.data.content);
            setTotalElements(response.data.totalElements);
            setNumberOfElements(response.data.numberOfElements);
        }
    };

    useEffect(() => {
        fetchData();
    }, [page, pageSize, sortModel]);

    const columns: GridColDef[] = [
        {
            flex: 2,
            minWidth: 100,
            field: 'dspPlatform',
            headerName: 'DSP Platform',
            valueGetter: (value: string) => {
                return value != null && value !== '' ? value : 'Other';
            },
            renderHeader: () => (
                <Tooltip title="DSP Platform" arrow>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: theme.palette.text.secondary,
                            fontWeight: theme.typography.fontWeightMedium,
                        }}
                    >
                        DSP Platform
                    </div>
                </Tooltip>
            ),
        },
        {
            flex: 3,
            minWidth: 100,
            field: 'cognitionInventoryCategory',
            headerName: 'Cognition Inventory Category',
            editable: true,
            valueGetter: (value: string) => {
                return value != null && value !== '' ? value : 'Other';
            },
            renderHeader: () => (
                <Tooltip title="Cognition Inventory Category" arrow>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: theme.palette.text.secondary,
                            fontWeight: theme.typography.fontWeightMedium,
                        }}
                    >
                        Cognition Inventory Category
                        <EditOutlinedIcon fontSize="small" style={{ marginLeft: 2 }} />
                    </div>
                </Tooltip>
            ),
        },
        {
            flex: 3,
            minWidth: 100,
            field: 'cognitionInventorySsp',
            headerName: 'Cognition Inventory Ssp',
            editable: true,
            valueGetter: (value: string) => {
                return value != null && value !== '' ? value : 'Other';
            },
            renderHeader: () => (
                <Tooltip title="Cognition Inventory Ssp" arrow>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: theme.palette.text.secondary,
                            fontWeight: theme.typography.fontWeightMedium,
                        }}
                    >
                        Cognition Inventory SSP
                        <EditOutlinedIcon fontSize="small" style={{ marginLeft: 2 }} />
                    </div>
                </Tooltip>
            ),
        },
        {
            flex: 3,
            minWidth: 100,
            field: 'cognitionInventoryContent',
            headerName: 'Cognition Inventory Content',
            editable: true,
            valueGetter: (value: string) => {
                return value != null && value !== '' ? value : 'Other';
            },
            renderHeader: () => (
                <Tooltip title="Cognition Inventory Content" arrow>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: theme.palette.text.secondary,
                            fontWeight: theme.typography.fontWeightMedium,
                        }}
                    >
                        Cognition Inventory Content
                        <EditOutlinedIcon fontSize="small" style={{ marginLeft: 2 }} />
                    </div>
                </Tooltip>
            ),
        },
        {
            flex: 3,
            minWidth: 100,
            field: 'cognitionInventoryType',
            headerName: 'Cognition Inventory Type',
            editable: true,
            valueGetter: (value: string) => {
                return value != null && value !== '' ? value : 'Other';
            },
            renderHeader: () => (
                <Tooltip title="Double click the value to edit it" arrow>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: theme.palette.text.secondary,
                            fontWeight: theme.typography.fontWeightMedium,
                        }}
                    >
                        Cognition Inventory Type
                        <EditOutlinedIcon fontSize="small" style={{ marginLeft: 2 }} />
                    </div>
                </Tooltip>
            ),
        },
        {
            flex: 3,
            minWidth: 100,
            field: 'cognitionPublisherName',
            headerName: 'Cognition Publisher Name',
            editable: true,
            valueGetter: (value: string) => {
                return value != null && value !== '' ? value : 'Other';
            },
            renderHeader: () => (
                <Tooltip title="Double click the value to edit it" arrow>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: theme.palette.text.secondary,
                            fontWeight: theme.typography.fontWeightMedium,
                        }}
                    >
                        Cognition Publisher Name
                        <EditOutlinedIcon fontSize="small" style={{ marginLeft: 2 }} />
                    </div>
                </Tooltip>
            ),
        },

        {
            flex: 3,
            minWidth: 100,
            field: 'exclude',
            headerName: 'Exclude',
            type: 'boolean',
            renderCell: (params) => {
                const value = params.value;
                return <Switch checked={value} onChange={handleSwitchChange(params.id as number)} />;
            },
        },
    ];

    return (
        <AccessGuard accessGroup={USER_GROUP_ADMIN}>
            <TabPanel index={tab.id} value={activeTab} sx={{ p: 0 }}>
                {/*<InfoMessage {...infoMessage} />*/}
                <Column gap={2}>
                    <Row justifyContent="space-between" alignItems="center">
                        <Row gap={0} sx={{ width: '100%' }}>
                            <Typography component="span" color="text.primary" variant="subtitle2" fontWeight={500}>
                                Instruction:{' '}
                                <Typography
                                    component="span"
                                    color="text.secondary"
                                    variant="subtitle2"
                                    fontWeight={500}
                                >
                                    {tab.description}
                                </Typography>
                            </Typography>
                        </Row>
                        <Row flex={0} justifyContent="flex-end">
                            {/*<Button variant="text" onClick={handleOpen} sx={{ marginTop: '-1%' }}>*/}
                            {/*    Add Macro*/}
                            {/*</Button>*/}
                            <FormControl sx={{ minWidth: 120 }} size="small">
                                <InputLabel id="demo-select-small-label">Rows Per Page</InputLabel>
                                <Select
                                    labelId="demo-select-small-label"
                                    id="demo-select-small"
                                    value={pageSize.toString()}
                                    label="Rows Per Page"
                                    onChange={handleChange}
                                >
                                    {pageValueArray?.map((value) => {
                                        return (
                                            <MenuItem key={value} value={value}>
                                                {value}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Row>
                    </Row>
                    <CDGrid
                        id="dspInventorySupplies"
                        variant="contained"
                        initialState={{ pagination: { paginationModel: { pageSize: pageSize, page: page } } }}
                        rowCount={totalElements}
                        columns={columns}
                        onPaginationModelChange={onPageChange}
                        paginationMode={'server'}
                        sortingMode={'server'}
                        onSortModelChange={setSortModel}
                        rows={list === null ? [] : list}
                        processRowUpdate={(newRow) => {
                            ApiService.updateDspInventorySupply(newRow);
                            setList((prevRows) =>
                                prevRows === null
                                    ? []
                                    : prevRows.map((row) => {
                                          if (row.id === newRow.id) {
                                              return newRow;
                                          } else {
                                              return row;
                                          }
                                      })
                            );
                            return newRow;
                        }}
                        onProcessRowUpdateError={() => {}}
                        slots={{
                            footer: () => (
                                <DspInventorySupplyGridFooter
                                    totalElements={totalElements}
                                    numberOfElements={numberOfElements}
                                />
                            ),
                        }}
                    />
                </Column>
            </TabPanel>
        </AccessGuard>
    );
};
export default DspInventorySupplyTab;
